import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import AnimatedHeight from '@/ui/components/estimatesAndProposals/animatedHeight/AnimatedHeight';
import { Accordion, H4, Text } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import {
  getAccountRegionFromCurrencyCodeLegacy,
  getRegionalTextString,
} from '@payaca/helpers/internationalHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import { RegionalStrings } from '@payaca/types/internationalTypes';
import { FC } from 'react';

type EstimateFinancialsProps = {
  projectId: string;
  estimateId: string;
};

const EstimateFinancials: FC<EstimateFinancialsProps> = (props) => {
  const { projectId, estimateId } = props;
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const { data: regionPreferences } = useGetMyRegionPreferences();

  if (!estimate) {
    return null;
  }

  const grants = estimate.grants ?? [];
  const region = regionPreferences
    ? getAccountRegionFromCurrencyCodeLegacy(regionPreferences.currency.code)
    : AccountRegions.UK;

  return (
    <div className="px-3.5 py-5 border-b">
      <Accordion
        title="Financials"
        chevronPosition="left"
        defaultOpen
        summary={
          <span className="flex gap-2">
            <Text as="span">
              {grants.length > 0 ? 'Total payable' : 'Total'}
            </Text>
            <Text as="span">
              <strong>
                <CurrencyValue {...estimate.totalPayable} />
              </strong>
            </Text>
          </span>
        }
      >
        <AnimatedHeight>
          <dl className="mt-2 grid grid-cols-2 items-end gap-y-1.5 m-0 [&>*]:m-0">
            <dt id="estimate-subtotal">
              <Text level="supporting">Subtotal</Text>
            </dt>
            <dd aria-labelledby="estimate-subtotal" className="text-right">
              <Text>
                <CurrencyValue {...estimate.subtotalExTax} />
              </Text>
            </dd>

            <dt id="estimate-tax" className="pb-1.5">
              <Text level="supporting">
                {getRegionalTextString(region, RegionalStrings.VALUE_ADDED_TAX)}
              </Text>
            </dt>
            <dd aria-labelledby="estimate-tax" className="text-right pb-1.5">
              <Text>
                <CurrencyValue {...estimate.totalTax} />
              </Text>
            </dd>

            {grants.map((grant) => (
              <GrantDeductionRow key={grant.id} grant={grant} />
            ))}

            <dt id="estimate-total" className="border-t pt-3.5">
              <H4>{grants.length > 0 ? 'Total payable' : 'Total'}</H4>
            </dt>
            <dd
              aria-labelledby="estimate-total"
              className="text-right border-t pt-3.5"
            >
              <H4>
                <CurrencyValue {...estimate.totalPayable} />
              </H4>
            </dd>
          </dl>
        </AnimatedHeight>
      </Accordion>
    </div>
  );
};

type GrantDeductionRowProps = {
  grant: NonNullable<
    ReturnType<typeof useGetProjectEstimate>['estimate']
  >['grants'][number];
};

const GrantDeductionRow: FC<GrantDeductionRowProps> = (props) => {
  const { grant } = props;
  const labelId = `estimate-grant-${grant.id}`;
  return (
    <>
      <dt id={labelId} className="pb-1.5">
        <Text level="supporting" colour="primary">
          {grant.name}
        </Text>
      </dt>
      <dd aria-labelledby={labelId} className="text-right pb-1.5">
        <Text colour="positive">
          −<CurrencyValue {...grant.amount} />
        </Text>
      </dd>
    </>
  );
};

export default EstimateFinancials;
